import { Search, Check, Filter, Settings, X } from "react-feather";
import { countries, Country } from "../../constants/countries";
import { AdminRangeSelector } from "./AdminRangeSelector";
import { useState, useRef, useEffect } from "react";

interface FilterOption {
  label: string;
  value: string;
}

interface RangeFilter {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  label?: string;
}

interface DateFilter {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
}

interface FilterGroup {
  label: string;
  options: FilterOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  isMulti?: boolean;
  showLabel?: boolean;
}

interface AdminFiltersProps {
  filters: FilterGroup[];
  searchPlaceholder?: string;
  onSearch?: (value: string) => void;
  searchValue?: string;
  showCountryFilter?: boolean;
  selectedCountry?: string | string[];
  onCountryChange?: (countryCode: string | string[]) => void;
  ageRange?: RangeFilter;
  revenueRange?: RangeFilter;
  dateFilter?: DateFilter;
  onReset?: () => void;
  onApply?: () => void;
}

export function AdminFilters({
  filters,
  searchPlaceholder = "Search",
  onSearch,
  searchValue = "",
  showCountryFilter = false,
  selectedCountry = "",
  onCountryChange,
  ageRange,
  revenueRange,
  dateFilter,
  onReset,
  onApply,
}: AdminFiltersProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [countrySearchValue, setCountrySearchValue] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isModalAnimating, setIsModalAnimating] = useState(false);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const countryFilter: FilterGroup | null = showCountryFilter
    ? {
        label: "Country",
        options: countries.map((country: Country) => ({
          label: country.name,
          value: country.code,
        })),
        value: selectedCountry,
        onChange: onCountryChange || (() => {}),
        isMulti: true,
      }
    : null;

  // Separate simple filters (first 2) from advanced filters
  const simpleFilters = filters.slice(0, 2);
  const advancedFilters = [...filters.slice(2)];
  if (countryFilter) {
    advancedFilters.push(countryFilter);
  }

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (openDropdown) {
        const currentRef = dropdownRefs.current[openDropdown];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Handle modal animation
  useEffect(() => {
    if (showAdvancedFilters) {
      // Small delay to ensure DOM is ready for animation
      const timer = setTimeout(() => {
        setIsModalAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [showAdvancedFilters]);

  const toggleDropdown = (label: string) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
    if (label === "Select Countries") {
      setCountrySearchValue("");
    }
  };

  const openAdvancedFilters = () => {
    setShowAdvancedFilters(true);
    setIsModalAnimating(true);
  };

  const closeAdvancedFilters = () => {
    setIsModalAnimating(false);
    // Delay hiding the modal to allow exit animation
    setTimeout(() => {
      setShowAdvancedFilters(false);
    }, 300);
  };

  const getSelectedOptionLabel = (filter: FilterGroup) => {
    if (filter.showLabel) return filter.label;
    if (!filter.value) return filter.label;
    const option = filter.options.find((opt) => opt.value === filter.value);
    return option ? option.label : filter.label;
  };

  const hasSelectedValue = (filter: FilterGroup) => {
    if (Array.isArray(filter.value)) {
      return filter.value.length > 0;
    }
    return (
      filter.value !== "" && filter.value !== null && filter.value !== undefined
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;

    // Count advanced filters
    advancedFilters.forEach((filter) => {
      if (hasSelectedValue(filter)) count++;
    });

    // Count range filters
    if (
      ageRange &&
      (ageRange.value[0] !== ageRange.min || ageRange.value[1] !== ageRange.max)
    ) {
      count++;
    }
    if (
      revenueRange &&
      (revenueRange.value[0] !== revenueRange.min ||
        revenueRange.value[1] !== revenueRange.max)
    ) {
      count++;
    }

    // Count date filter
    if (dateFilter && dateFilter.value) {
      count++;
    }

    return count;
  };

  const CustomDropdown = ({
    filter,
    isSimple = false,
  }: {
    filter: FilterGroup;
    isSimple?: boolean;
  }) => (
    <div
      className={`relative ${isSimple ? "w-40" : "w-full"}`}
      ref={(el) => (dropdownRefs.current[filter.label] = el)}
    >
      <div
        onClick={() => toggleDropdown(filter.label)}
        className={`w-full px-3 py-2.5 border rounded-lg text-sm cursor-pointer flex justify-between items-center transition-all duration-200 ${
          hasSelectedValue(filter)
            ? "border-orange-300 dark:border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-muted-foreground shadow-sm"
            : "border-border bg-card text-muted-foreground hover:border-ring"
        }`}
      >
        <span className="font-medium truncate">
          {getSelectedOptionLabel(filter)}
        </span>
        <svg
          className={`h-4 w-4 text-muted-foreground ml-2 flex-shrink-0 transition-transform duration-200 ${
            openDropdown === filter.label ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {openDropdown === filter.label && (
        <div className="absolute z-50 mt-2 w-full bg-card border border-border rounded-xl shadow-xl overflow-hidden">
          <div className="p-3">
            <div className="text-xs font-semibold text-muted-foreground mb-3">
              {filter.label === "Country" ? "Select Countries" : filter.label}
            </div>

            {filter.label === "Country" && (
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-muted-foreground" />
                </div>
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearchValue}
                  onChange={(e) => setCountrySearchValue(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring/20 bg-muted"
                />
              </div>
            )}

            <div
              className={
                filter.label === "Country" ? "max-h-48 overflow-y-auto" : ""
              }
            >
              {filter.options
                .filter((option) => {
                  if (filter.label === "Country" && countrySearchValue) {
                    return option.label
                      .toLowerCase()
                      .includes(countrySearchValue.toLowerCase());
                  }
                  return true;
                })
                .map((option) => (
                  <div
                    key={option.value}
                    className={`flex items-center px-3 py-2.5 hover:bg-accent cursor-pointer rounded-lg transition-colors ${
                      Array.isArray(filter.value)
                        ? filter.value.includes(option.value)
                          ? "bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30"
                          : ""
                        : filter.value === option.value
                        ? "bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30"
                        : ""
                    }`}
                    onClick={() => {
                      if (filter.isMulti) {
                        const currentValue = Array.isArray(filter.value)
                          ? filter.value
                          : [];
                        const newValue = currentValue.includes(option.value)
                          ? currentValue.filter((v) => v !== option.value)
                          : [...currentValue, option.value];
                        filter.onChange(newValue);
                      } else {
                        filter.onChange(option.value);
                        setOpenDropdown(null);
                      }
                    }}
                  >
                    <div className="w-4 h-4 mr-3 flex items-center justify-center border-2 border-border rounded">
                      {Array.isArray(filter.value)
                        ? filter.value.includes(option.value) && (
                            <Check
                              className="h-3 w-3"
                              style={{ color: "#d7785b" }}
                            />
                          )
                        : filter.value === option.value && (
                            <Check
                              className="h-3 w-3"
                              style={{ color: "#d7785b" }}
                            />
                          )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {option.label}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="w-full bg-muted rounded-xl p-4 border border-border">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearch?.(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/20 text-sm bg-card placeholder-muted-foreground shadow-sm"
              onFocus={(e) => {
                e.target.style.borderColor = "#d7785b";
                e.target.style.boxShadow = "0 0 0 3px rgba(215, 120, 91, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d6d3d1";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Simple Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {simpleFilters.map((filter) => (
              <CustomDropdown key={filter.label} filter={filter} isSimple />
            ))}

            {/* Advanced Filters Button */}
            <button
              onClick={openAdvancedFilters}
              className="flex items-center gap-2 px-4 py-3 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:border-ring hover:bg-card transition-all duration-200 bg-card shadow-sm"
            >
              <Settings className="h-4 w-4" />
              Advanced Filters
              {getActiveFiltersCount() > 0 && (
                <span
                  className="px-2 py-0.5 text-xs font-semibold text-white rounded-full"
                  style={{ backgroundColor: "#d7785b" }}
                >
                  {getActiveFiltersCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
              isModalAnimating ? "bg-opacity-50" : "bg-opacity-0"
            }`}
            onClick={closeAdvancedFilters}
          />

          {/* Modal Panel */}
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-md bg-card shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
              isModalAnimating ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border bg-muted">
                <div className="flex items-center gap-3">
                  <Filter className="h-5 w-5 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">
                    Advanced Filters
                  </h2>
                </div>
                <button
                  onClick={closeAdvancedFilters}
                  className="p-2 text-muted-foreground hover:text-muted-foreground hover:bg-accent rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Advanced Filter Groups */}
                {advancedFilters.map((filter) => (
                  <div key={filter.label} className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">
                      {filter.label}
                    </label>
                    <CustomDropdown filter={filter} />
                  </div>
                ))}

                {/* Age Range */}
                {ageRange && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-muted-foreground">
                      Age Range
                    </label>
                    <div className="px-2">
                      <AdminRangeSelector
                        min={ageRange.min}
                        max={ageRange.max}
                        value={ageRange.value}
                        onChange={ageRange.onChange}
                        step={1}
                      />
                    </div>
                  </div>
                )}

                {/* Revenue Range */}
                {revenueRange && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-muted-foreground">
                      {revenueRange.label || "Revenue Range"}
                    </label>
                    <div className="px-2">
                      <AdminRangeSelector
                        min={revenueRange.min}
                        max={revenueRange.max}
                        value={revenueRange.value}
                        onChange={revenueRange.onChange}
                        step={100}
                      />
                    </div>
                  </div>
                )}

                {/* Date Filter */}
                {dateFilter && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">
                      {dateFilter.label}
                    </label>
                    <input
                      type="date"
                      value={dateFilter.value}
                      onChange={(e) => dateFilter.onChange(e.target.value)}
                      placeholder={dateFilter.placeholder}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring/20 bg-card"
                      onFocus={(e) => {
                        e.target.style.borderColor = "#d7785b";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(215, 120, 91, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d6d3d1";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-border bg-muted">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      onReset?.();
                      closeAdvancedFilters();
                    }}
                    className="flex-1 px-4 py-3 text-sm font-medium border-2 border-border text-muted-foreground rounded-lg hover:border-ring hover:bg-card transition-all duration-200"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => {
                      onApply?.();
                      closeAdvancedFilters();
                    }}
                    className="flex-1 px-4 py-3 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    style={{ backgroundColor: "#d7785b" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#c66a50";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#d7785b";
                    }}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
